/**
 * webCOA "Modern" layout engine.
 *
 * Classic keeps golden-layout. Modern uses this self-contained CSS-Grid
 * layout INSTEAD, so it sidesteps every golden-layout docking quirk (the
 * viewport/options gap, panes that get stuck, components that can't be
 * grabbed) -- because golden-layout is never instantiated in this mode.
 *
 * The panes are real DOM cells whose bodies are cloned from the very same
 * <template> elements golden-layout used, so all existing handlers keep
 * working unchanged (they find #client_log, #client_musiclist, etc. by id).
 *
 * Resizable, not free-floating: adjacent regions can be dragged via thin
 * splitters (a deliberate, reliable subset of "dockable" rather than the
 * buggy full docking). Mobile collapses to a single scrolling column via
 * CSS.
 */

interface PanelDef {
  id: string;
  title: string;
  area: string;
}

const MAIN_PANELS: PanelDef[] = [
  { id: "client_wrapper", title: "Courtroom", area: "viewport" },
  { id: "icoptions", title: "IC Options", area: "options" },
  { id: "log", title: "Record", area: "log" },
  { id: "ooc", title: "OOC", area: "ooc" },
];

// Bottom-left section (under the log): Main and Players.
const MAINPLAYERS_TABS: PanelDef[] = [
  { id: "mainmenu", title: "Main", area: "" },
  { id: "players", title: "Players", area: "" },
];
const DEFAULT_MAINPLAYERS_TAB = "mainmenu";

// Bottom-right section: an A/M toggle between the Areas list and Music,
// like the AO2 client. "__areas__" has no template -- the #areas list is
// moved into it after the layout is built (see buildModernLayout).
const AREASMUSIC_TABS: PanelDef[] = [
  { id: "__areas__", title: "Areas", area: "" },
  { id: "music", title: "Music", area: "" },
];
const DEFAULT_AREASMUSIC_TAB = "__areas__";

function cloneTemplateInto(host: HTMLElement, templateId: string): void {
  if (templateId.startsWith("__")) return; // placeholder body, filled later
  const tpl = document.querySelector<HTMLTemplateElement>(`#${templateId}`);
  if (tpl) host.innerHTML = tpl.innerHTML;
}

function makePanel(def: PanelDef): HTMLElement {
  const cell = document.createElement("section");
  cell.className = "coa-panel";
  cell.style.gridArea = def.area;

  const head = document.createElement("header");
  head.className = "coa-panel-head";
  head.textContent = def.title;

  const body = document.createElement("div");
  body.className = "coa-panel-body";
  cloneTemplateInto(body, def.id);

  cell.appendChild(head);
  cell.appendChild(body);
  return cell;
}

/** A tabbed panel for a given grid area. Returns the section element. */
function makeTabbedSection(
  gridArea: string,
  className: string,
  tabs: PanelDef[],
  defaultTabId: string,
): HTMLElement {
  const cell = document.createElement("section");
  cell.className = `coa-panel ${className}`;
  cell.style.gridArea = gridArea;

  const tabbar = document.createElement("div");
  tabbar.className = "coa-tabbar";
  cell.appendChild(tabbar);

  const bodies: HTMLElement[] = [];
  tabs.forEach((tab) => {
    const isDefault = tab.id === defaultTabId;
    const btn = document.createElement("button");
    btn.className = "coa-tab" + (isDefault ? " active" : "");
    btn.type = "button";
    btn.textContent = tab.title;

    const body = document.createElement("div");
    body.className = "coa-panel-body coa-tab-body";
    body.dataset.tab = tab.id;
    body.style.display = isDefault ? "" : "none";
    cloneTemplateInto(body, tab.id);
    bodies.push(body);

    btn.addEventListener("click", () => {
      tabbar
        .querySelectorAll(".coa-tab")
        .forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      bodies.forEach((b) => (b.style.display = "none"));
      body.style.display = "";
    });

    tabbar.appendChild(btn);
    cell.appendChild(body);
  });

  return cell;
}

/** Builds and mounts the Modern grid. Call instead of golden-layout. */
export function buildModernLayout(): void {
  const root = document.createElement("div");
  root.className = "coa-modern-grid";
  root.id = "coa-modern-root";

  for (const def of MAIN_PANELS) root.appendChild(makePanel(def));
  root.appendChild(
    makeTabbedSection(
      "mainplyr",
      "coa-mainplayers",
      MAINPLAYERS_TABS,
      DEFAULT_MAINPLAYERS_TAB,
    ),
  );
  const areasMusic = makeTabbedSection(
    "areasmusic",
    "coa-areasmusic",
    AREASMUSIC_TABS,
    DEFAULT_AREASMUSIC_TAB,
  );
  root.appendChild(areasMusic);

  document.body.appendChild(root);

  // Move the #areas list out of the Main tab's menu and into the Areas/Music
  // section's Areas tab (keeps a single #areas element so handleARUP still
  // finds it; Classic is untouched since it builds its own clone separately).
  const areasEl = root.querySelector("#areas");
  const areasBody = areasMusic.querySelector<HTMLElement>(
    '.coa-tab-body[data-tab="__areas__"]',
  );
  if (areasEl && areasBody) {
    areasBody.appendChild(areasEl);
  }

  // In Modern the area list has moved to the Areas/Music section, so the
  // Main menu's old "Areas" entry (now just the background preview) is
  // renamed to "Background". Classic keeps "Areas" since its list stays put.
  const bgMenuText = root.querySelector<HTMLElement>("#menu_1 .menu_text");
  if (bgMenuText) bgMenuText.textContent = "Background";
  const bgIcon = root.querySelector<HTMLElement>("#menu_1 .menu_icon");
  if (bgIcon) bgIcon.innerHTML = "&#128444;";
}
